import type { DataTableSort } from "@/shared/foundation/primitives/Table";

import type { ResourceView } from "./useResourceView";

/**
 * The parts of a resource view a customer can move: filters, sort, page size,
 * columns. Each is `useState`-backed in `useResourceView` / `useTable`, so a
 * reference that has not changed means the customer has not touched it.
 *
 * A fingerprint, not a copy: it exists to be compared, never read. The
 * serialized record a tab remembers is `TableSnapshot` in `tableMemory`.
 */
export interface TableFingerprint {
  filters: unknown;
  sort: DataTableSort | null;
  pageSize: number;
  visibleColumnIds: ReadonlySet<string>;
}

export function fingerprint<TData>(
  view: ResourceView<TData>,
): TableFingerprint {
  return {
    filters: view.filters,
    sort: view.table.sort,
    pageSize: view.table.pageSize,
    visibleColumnIds: view.table.visibleColumnIds,
  };
}

/**
 * Whether the view moved between two fingerprints.
 *
 * Sort is compared by value rather than reference: re-choosing the sort it
 * already had is not a change worth acting on. The rest are reference
 * comparisons, which is sound because each is `useState`-backed.
 */
export function hasMovedSince(
  before: TableFingerprint,
  now: TableFingerprint,
): boolean {
  return (
    before.filters !== now.filters ||
    before.pageSize !== now.pageSize ||
    before.visibleColumnIds !== now.visibleColumnIds ||
    before.sort?.columnId !== now.sort?.columnId ||
    before.sort?.direction !== now.sort?.direction
  );
}
